import { AnyAction, createAsyncThunk, ThunkDispatch } from '@reduxjs/toolkit';

import ERRORS from '~/constants/errors';

import { RETRY_MESSAGE } from '../constants/domain-specific';
import { decline, genericDecline } from '../slices/decline';
import { paymentsSlice } from '../slices/payments/slice';
import { MulesoftError, MulesoftErrorData } from '../types/error';
import { PaymentErrorCode } from '../types/payments';
import { isFetchBaseQueryErrorType } from '../utils';
import { logError } from '../utils/logError';

const paymentErrorCodeToMessage = {
  [PaymentErrorCode.F1]: 'Invalid BSB. Please try again.',
  [PaymentErrorCode.MS1]: 'Bind successful, Payment failed.',
  [PaymentErrorCode.PAYMENT_ERROR_RETRY]: RETRY_MESSAGE,
  [PaymentErrorCode.PAYMENT_ERROR_SYSTEM]: RETRY_MESSAGE,
  [PaymentErrorCode.UNKNOWN]: RETRY_MESSAGE,
  [PaymentErrorCode.PAYMENT_ERROR_FAILED]: RETRY_MESSAGE,
};

export const getPaymentErrorCodeAndMessage = (code: string) => {
  const paymentErrorCode = code as PaymentErrorCode;
  return {
    code: paymentErrorCode,
    message: paymentErrorCodeToMessage[paymentErrorCode],
  };
};

const paymentErrorExists = (code: string) => Object.keys(PaymentErrorCode).includes(code);

const handleErrors = <T extends ThunkDispatch<unknown, unknown, AnyAction>>(
  errors: MulesoftErrorData[],
  dispatch: T
) => {
  const err = errors[0];
  if (paymentErrorExists(err.code)) {
    const { code, message } = getPaymentErrorCodeAndMessage(err.code);
    dispatch(
      paymentsSlice.actions.addPaymentError({
        code,
        message,
      })
    );
    return;
  }
  if (err.extendedMessages) {
    const paymentErrors = err.extendedMessages.filter(({ code }) => paymentErrorExists(code));
    if (paymentErrors.length > 0) {
      paymentErrors.forEach((paymentError) => {
        const { code, message } = getPaymentErrorCodeAndMessage(paymentError.code);
        if (code === PaymentErrorCode.MS1) {
          dispatch(
            decline({
              code,
              message,
            })
          );
          return;
        }
        dispatch(
          paymentsSlice.actions.addPaymentError({
            code,
            message,
          })
        );
      });
      return;
    }
  }
  dispatch(decline({ message: JSON.stringify(errors) }));
};

export const handleError = createAsyncThunk<void, unknown>(
  'handle-error',
  async (error, { dispatch }) => {
    logError(error);
    if (isFetchBaseQueryErrorType(error)) {
      const errData = error.data as MulesoftError;

      if ('errors' in errData) {
        handleErrors(errData.errors, dispatch);
      } else if ('error' in errData) {
        dispatch(decline({ message: errData.error }));
      } else {
        dispatch(decline(ERRORS.malformedMulesoftError));
      }
    } else {
      dispatch(genericDecline());
    }
  }
);

export const isPaymentErrorRetry = (action: AnyAction) =>
  action.payload?.data?.errors?.[0]?.code === PaymentErrorCode.PAYMENT_ERROR_RETRY;
